import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import { Modal } from './ui/Modal';
import { Input, Label } from './ui';
import { optionsEncargadoProyecto, optionsAreaResponsable } from '../datos/options';
import { MOTIVOS_VISITA, colorVisitaPorArea } from '../constants/config';

// Opciones de horas fijas (cada 30 min)
const HORAS_FIJAS = [];
for (let h = 8; h <= 18; h++) {
    HORAS_FIJAS.push(`${String(h).padStart(2, '0')}:00`);
    HORAS_FIJAS.push(`${String(h).padStart(2, '0')}:30`);
}

// Cuartos de hora adicionales (15/45) habilitados por el checkbox
const CUARTOS_HORA = [];
for (let h = 8; h <= 18; h++) {
    CUARTOS_HORA.push(`${String(h).padStart(2, '0')}:15`);
    CUARTOS_HORA.push(`${String(h).padStart(2, '0')}:45`);
}

// Helper para armar un Date local a partir de "YYYY-MM-DD" (ignora la parte horaria/UTC)
const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return isNaN(dateInput.getTime()) ? null : dateInput;
    const [y, m, d] = String(dateInput).split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

// Estilos para react-select consistentes con formA
const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '40px',
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        borderWidth: '1px',
        borderRadius: '0.375rem',
        boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
        },
        backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? '#3b82f6'
            : state.isFocused
                ? '#e5e7eb'
                : document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
        color: state.isSelected
            ? '#ffffff'
            : document.documentElement.classList.contains('dark') ? '#ffffff' : '#111827',
        '&:hover': {
            backgroundColor: state.isSelected ? '#3b82f6' : '#e5e7eb',
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '0.375rem',
        border: '1px solid #d1d5db',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        backgroundColor: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#dbeafe',
        borderRadius: '0.25rem',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#1e40af',
        fontSize: '0.875rem',
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#1e40af',
        '&:hover': {
            backgroundColor: '#3b82f6',
            color: '#ffffff',
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: '#9ca3af',
        fontSize: '0.875rem',
    }),
    input: (base) => ({
        ...base,
        color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#111827',
        fontSize: '0.875rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#111827',
        fontSize: '0.875rem',
    }),
};

const ModalVisita = ({ isOpen, onClose, token, id_tabla_obras, obraLabel, fechaInicial, onGuardada, opcionesObras = [], visitaEditable = null }) => {
    const esEdicion = Boolean(visitaEditable);

    const [motivo, setMotivo] = useState([]);
    const [observaciones, setObservaciones] = useState('');
    const [fecha, setFecha] = useState(fechaInicial || null);
    const [fechaHasta, setFechaHasta] = useState(null);
    const [esRango, setEsRango] = useState(false);
    const [hora, setHora] = useState('');
    const [permitirCuartos, setPermitirCuartos] = useState(false);
    const [personal, setPersonal] = useState([]);
    const [obraId, setObraId] = useState(id_tabla_obras || '');
    const [busquedaObra, setBusquedaObra] = useState('');
    const [areaResponsable, setAreaResponsable] = useState(''); // área manual cuando la visita no tiene obra
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    useEffect(() => {
        if (!isOpen) return;
        setError('');

        if (visitaEditable) {
            setMotivo(Array.isArray(visitaEditable.motivo)
                ? visitaEditable.motivo.map(m => ({ value: m, label: m }))
                : (visitaEditable.motivo ? [{ value: visitaEditable.motivo, label: visitaEditable.motivo }] : []));
            setObservaciones(visitaEditable.observaciones || '');
            setFecha(parseLocalDate(visitaEditable.fecha));
            setFechaHasta(parseLocalDate(visitaEditable.fecha_hasta));
            setEsRango(Boolean(visitaEditable.fecha_hasta));
            setHora(visitaEditable.hora ? String(visitaEditable.hora).slice(0, 5) : '');
            setPermitirCuartos(String(visitaEditable.hora || '').slice(3) === '15' || String(visitaEditable.hora || '').slice(3) === '45');
            setPersonal((visitaEditable.personal || []).map(p => ({
                value: p.name,
                label: p.name,
            })));
            setObraId(visitaEditable.id_tabla_obras || '');
            setAreaResponsable(visitaEditable.area_responsable || '');
        } else {
            setMotivo([]);
            setObservaciones('');
            setFecha(fechaInicial || null);
            setFechaHasta(null);
            setEsRango(false);
            setHora('');
            setPermitirCuartos(false);
            setPersonal([]);
            setObraId(id_tabla_obras || '');
            setAreaResponsable('');
        }
        setBusquedaObra('');
    }, [isOpen, fechaInicial, id_tabla_obras, visitaEditable]);

    const idObraFinal = visitaEditable?.id_tabla_obras || id_tabla_obras || obraId;
    const obraSeleccionada = opcionesObras.find(o => String(o.id) === String(idObraFinal));
    const areaObra = obraSeleccionada?.area_responsable || '';
    const areaEfectiva = areaObra || areaResponsable || '';
    const colorArea = colorVisitaPorArea(areaEfectiva);

    const resultadosBusqueda = busquedaObra.trim()
        ? opcionesObras.filter(o =>
            (o.id_partido_obra && String(o.id_partido_obra).toLowerCase().includes(busquedaObra.trim().toLowerCase())) ||
            (o.nombre_obra && String(o.nombre_obra).toLowerCase().includes(busquedaObra.trim().toLowerCase()))
        ).slice(0, 10)
        : [];

    const horasDisponibles = permitirCuartos
        ? [...HORAS_FIJAS, ...CUARTOS_HORA].sort()
        : HORAS_FIJAS;

    const guardar = async () => {
        if (!fecha) {
            setError('Debe seleccionar una fecha');
            return;
        }
        if (esRango && !fechaHasta) {
            setError('Debe seleccionar la fecha Hasta del rango');
            return;
        }
        if (esRango && fechaHasta && fechaHasta < fecha) {
            setError('La fecha Hasta debe ser posterior o igual a la fecha Desde');
            return;
        }
        if (!idObraFinal && !areaResponsable) {
            setError('Debe seleccionar una obra o un área responsable');
            return;
        }
        setGuardando(true);
        setError('');
        try {
            const anio = fecha.getFullYear();
            const mes = String(fecha.getMonth() + 1).padStart(2, '0');
            const dia = String(fecha.getDate()).padStart(2, '0');
            let fechaHastaStr = null;
            if (esRango && fechaHasta) {
                fechaHastaStr = `${fechaHasta.getFullYear()}-${String(fechaHasta.getMonth() + 1).padStart(2, '0')}-${String(fechaHasta.getDate()).padStart(2, '0')}`;
            }
            const body = {
                id_tabla_obras: idObraFinal || null,
                area_responsable: areaObra || areaResponsable || null,
                motivo: (motivo && motivo.length > 0) ? motivo.map(m => m.value ?? m.label ?? m) : null,
                fecha: `${anio}-${mes}-${dia}`,
                fecha_hasta: fechaHastaStr,
                hora: hora || null,
                personal: personal.map(p => p.value ?? p.label ?? p),
                observaciones: observaciones || null,
            };
            const url = esEdicion ? `/api/visitas/${visitaEditable.id}` : '/api/visitas';
            const res = await fetch(url, {
                method: esEdicion ? 'PUT' : 'POST',
                headers,
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error(esEdicion ? 'Error al actualizar la visita' : 'Error al guardar la visita');
            if (onGuardada) onGuardada();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setGuardando(false);
        }
    };

    const eliminar = async () => {
        if (!window.confirm('¿Seguro que desea eliminar esta visita?')) return;
        setGuardando(true);
        setError('');
        try {
            const res = await fetch(`/api/visitas/${visitaEditable.id}`, {
                method: 'DELETE',
                headers,
            });
            if (!res.ok) throw new Error('Error al eliminar la visita');
            if (onGuardada) onGuardada();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setGuardando(false);
        }
    };

    const titulo = esEdicion
        ? `Editar visita${obraLabel || obraSeleccionada?.label ? ` — ${obraLabel || obraSeleccionada?.label}` : ''}`
        : `Agendar visita${obraLabel || obraSeleccionada?.label ? ` — ${obraLabel || obraSeleccionada?.label}` : ''}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={titulo}>
            <div className="space-y-4">
                {!id_tabla_obras && !esEdicion && (
                    <div className="space-y-3">
                        <div>
                            <Label>Buscar obra (por ID o nombre)</Label>
                            <Input
                                type="text"
                                value={busquedaObra}
                                onChange={(e) => {
                                    setBusquedaObra(e.target.value);
                                    setObraId('');
                                }}
                                placeholder="Escriba id_partido_obra (ej. 026-003) o nombre de la obra..."
                            />
                            {busquedaObra.trim() && !obraId && (
                                <div className="mt-1 border border-gray-300 dark:border-gray-600 rounded-md max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                                    {resultadosBusqueda.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Sin resultados</div>
                                    ) : (
                                        resultadosBusqueda.map(o => (
                                            <button
                                                key={o.id}
                                                type="button"
                                                onClick={() => {
                                                    setObraId(o.id);
                                                    setBusquedaObra(o.label);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                                            >
                                                {o.label}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                            {areaObra && (
                                <div className="mt-2 flex items-center gap-2">
                                    <span
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium"
                                        style={{ backgroundColor: colorArea.hex, borderColor: colorArea.border }}
                                    >
                                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorArea.hex, borderColor: colorArea.border }}></span>
                                        {colorArea.label}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Área responsable que se guardará en la visita
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!idObraFinal && (
                    <div>
                        <Label htmlFor="areaResponsable">Área responsable (sin obra vinculada)</Label>
                        <Select
                            id="areaResponsable"
                            options={optionsAreaResponsable}
                            value={optionsAreaResponsable.find(o => o.value === areaResponsable) || null}
                            onChange={(selected) => setAreaResponsable(selected?.value || '')}
                            placeholder="Seleccionar área responsable..."
                            styles={customSelectStyles}
                            className="mt-1"
                        />
                        {areaResponsable && (
                            <div className="mt-2 flex items-center gap-2">
                                <span
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium"
                                    style={{ backgroundColor: colorArea.hex, borderColor: colorArea.border }}
                                >
                                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorArea.hex, borderColor: colorArea.border }}></span>
                                    {colorArea.label}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Área responsable que se guardará en la visita
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <Label htmlFor="motivo">Motivo (puede seleccionar varios)</Label>
                    <Select
                        id="motivo"
                        isMulti
                        options={[
                            ...new Set([
                                ...MOTIVOS_VISITA,
                                ...(motivo.map(m => m?.value ?? m?.label)).filter(Boolean),
                            ])
                        ].map(m => ({ value: m, label: m }))}
                        value={motivo}
                        onChange={(selected) => setMotivo(selected || [])}
                        placeholder="Seleccionar motivos..."
                        styles={customSelectStyles}
                        className="mt-1"
                    />
                    {motivo.length > 0 && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Seleccionados: {motivo.map(m => m.label).join(', ')}
                        </p>
                    )}
                 </div>

                <div>
                    <Label htmlFor="observaciones">Observaciones</Label>
                    <textarea
                        id="observaciones"
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        rows={3}
                        placeholder="Observaciones de la visita (opcional)..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 resize-y"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="fecha">Fecha</Label>
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <input
                                type="checkbox"
                                checked={esRango}
                                onChange={(e) => setEsRango(e.target.checked)}
                                className="h-4 w-4"
                            />
                            Rango de fechas (desde / hasta)
                        </label>
                    </div>
                    {esRango ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Desde</Label>
                                <DatePicker
                                    selected={fecha}
                                    onChange={setFecha}
                                    dateFormat="dd/MM/yyyy"
                                    minDate={new Date()}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Hasta</Label>
                                <DatePicker
                                    selected={fechaHasta}
                                    onChange={setFechaHasta}
                                    dateFormat="dd/MM/yyyy"
                                    minDate={fecha || new Date()}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                                />
                            </div>
                        </div>
                    ) : (
                        <DatePicker
                            selected={fecha}
                            onChange={setFecha}
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                        />
                    )}
                </div>

                <div>
                    <Label htmlFor="hora">Hora</Label>
                    <div className="flex items-center gap-4">
                        <select
                            id="hora"
                            value={hora}
                            onChange={(e) => setHora(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700 h-10"
                        >
                            <option value="">Sin hora</option>
                            {horasDisponibles.map(h => (
                                <option key={h} value={h}>{h}</option>
                            ))}
                        </select>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            <input
                                type="checkbox"
                                checked={permitirCuartos}
                                onChange={(e) => setPermitirCuartos(e.target.checked)}
                                className="h-4 w-4"
                            />
                            Mostrar cuartos (10:15 / 10:45)
                        </label>
                    </div>
                </div>

                <div>
                    <Label htmlFor="personal">Personal asignado</Label>
                    <Select
                        id="personal"
                        isMulti
                        options={optionsEncargadoProyecto}
                        value={personal}
                        onChange={(selected) => setPersonal(selected || [])}
                        placeholder="Seleccionar personal..."
                        styles={customSelectStyles}
                        className="mt-1"
                    />
                    {personal.length > 0 && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Seleccionados: {personal.map(p => p.label).join(', ')}
                        </p>
                    )}
                </div>

                {error && <p className="text-red-600 text-sm">{error}</p>}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md transition-colors"
                    >
                        Cancelar
                    </button>
                    {esEdicion && (
                        <button
                            type="button"
                            onClick={eliminar}
                            disabled={guardando}
                            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
                        >
                            Eliminar
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={guardar}
                        disabled={guardando}
                        className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                        {guardando ? 'Guardando...' : (esEdicion ? 'Guardar cambios' : 'Guardar visita')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ModalVisita;
